import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import { useConfirmTossPayments } from "hooks/useConfirmTossPayments";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import useIsInappWebview from "hooks/useIsInappWebview";
import { sendMessage } from "utils/webview";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { trackCapiStandardEvent } from "../../analytics/metaCapi";

const hasPaymentSuccessMessage = (message: any) => {
  if (Array.isArray(message)) {
    return message.some(hasPaymentSuccessMessage);
  }

  return message?.event === "payment" && message?.params?.status === "success";
};

export default function TossPaymentsSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isInappWebview = useIsInappWebview();
  const parsedMessageFromApp = useGetEventFromApp();
  const user = useRecoilValue(userAtom);
  const shouldSendCloseAllRef = useRef(false);

  const {
    mutate: confirmPayments,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useConfirmTossPayments();

  const errorMessage =
    (error && typeof error === "object" && "message" in error
      ? (error as any).message
      : undefined) || undefined;

  const sendTossPaymentsMessage = (params: Record<string, any>) => {
    shouldSendCloseAllRef.current = params?.status === "success";

    sendMessage(
      JSON.stringify({
        event: "toss_payments",
        params,
      })
    );
  };

  useEffect(() => {
    if (!isInappWebview) return;
    if (!shouldSendCloseAllRef.current) return;
    if (!hasPaymentSuccessMessage(parsedMessageFromApp)) return;

    shouldSendCloseAllRef.current = false;

    setTimeout(() => {
      sendMessage(
        JSON.stringify({
          event: "close_all",
        })
      );
    }, 2000);
  }, [isInappWebview, parsedMessageFromApp]);

  // 결제 확인은 성공 페이지 진입 시 1회만 호출합니다.
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const payAmount = Number(searchParams.get("amount"));

    if (orderId && paymentKey && payAmount) {
      // 결제 확인 API 호출
      confirmPayments(
        { orderId, paymentKey, payAmount },
        {
          onSuccess: () => {
            // 결제 성공 시, 결제 전까지 불변이어야 했던 로컬스토리지 키 초기화
            try {
              // 결제 성공 후 재진입 시 결제 페이지의 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송을 1회 차단
              localStorage.setItem("suppress_payment_page_view", "1");
            } catch (e) {}

            if (user?.isInsider !== true) {
              trackCapiStandardEvent(
                "Purchase",
                {
                  value: payAmount,
                  currency: "KRW",
                  externalId: user?.userId,
                  email: user?.email,
                },
                {
                  eventId: orderId ?? undefined,
                },
                undefined,
                true
              );
            }

            if (isInappWebview) {
              if (user?.isInsider === true) {
                sendTossPaymentsMessage({
                  status: "success",
                });
              } else {
                if (user?.segmentType) {
                  if (user?.segmentType === "a") {
                    sendTossPaymentsMessage({
                      status: "success",
                      logEventList: [
                        {
                          eventName: `program_start_type_${user?.segmentType}`,
                          value: payAmount,
                          currency: "KRW",
                        },
                        {
                          eventName: "program_start",
                          value: payAmount,
                          currency: "KRW",
                        },
                        {
                          eventName: "debug_start",
                          value: payAmount,
                          currency: "KRW",
                          byOs: true,
                        },
                      ],
                      firebaseAnalyticsEventList: [
                        {
                          eventName: "purchase",
                          eventParams: {
                            transaction_id: orderId,
                          },
                        },
                        {
                          eventName: `purchase_type_${user?.segmentType}`,
                          eventParams: {
                            transaction_id: `PT_${orderId}_${user?.segmentType}`,
                          },
                        },
                      ],
                      appsflyerEventList: [
                        {
                          eventName: "af_program_start",
                          eventParams: {
                            af_content_id: "distancing_program",
                            af_order_id: orderId,
                            af_currency: "KRW",
                            af_revenue: payAmount,
                          },
                        },
                      ],
                    });
                  } else {
                    sendTossPaymentsMessage({
                      status: "success",
                      logEventList: [
                        {
                          eventName: `program_start_type_${user?.segmentType}`,
                          value: payAmount,
                          currency: "KRW",
                        },
                        {
                          eventName: "program_start",
                          value: payAmount,
                          currency: "KRW",
                        },
                        {
                          eventName: "debug_start",
                          value: payAmount,
                          currency: "KRW",
                          byOs: true,
                        },
                      ],
                      firebaseAnalyticsEventList: [
                        {
                          eventName: "purchase",
                          eventParams: {
                            transaction_id: orderId,
                          },
                        },
                        {
                          eventName: `purchase_type_${user?.segmentType}`,
                          eventParams: {
                            transaction_id: `PT_${orderId}_${user?.segmentType}`,
                          },
                        },
                      ],
                      appsflyerEventList: [
                        {
                          eventName: "af_program_start",
                          eventParams: {
                            af_content_id: "distancing_program",
                            af_order_id: orderId,
                            af_currency: "KRW",
                            af_revenue: payAmount,
                          },
                        },
                      ],
                    });
                  }
                } else {
                  sendTossPaymentsMessage({
                    status: "success",
                    logEventList: [
                      {
                        eventName: "program_start",
                        value: payAmount,
                        currency: "KRW",
                      },
                      {
                        eventName: "debug_start",
                        value: payAmount,
                        currency: "KRW",
                        byOs: true,
                      },
                    ],
                    firebaseAnalyticsEventList: [
                      {
                        eventName: "purchase",
                        eventParams: {
                          transaction_id: orderId,
                        },
                      },
                    ],
                    appsflyerEventList: [
                      {
                        eventName: "af_program_start",
                        eventParams: {
                          af_content_id: "distancing_program",
                          af_order_id: orderId,
                          af_currency: "KRW",
                          af_revenue: payAmount,
                        },
                      },
                    ],
                  });
                }
              }
            } else {
              navigate("/", { replace: true });
            }
          },
        }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 로딩/성공/실패 UI 렌더
  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center" spacing={1.5}>
            <div>결제 처리 중 오류가 발생했습니다.</div>
            {errorMessage && <div>{errorMessage}</div>}
            <Button
              color="primary"
              onClick={() => {
                if (isInappWebview) {
                  sendMessage(
                    JSON.stringify({
                      event: "toss_payments",
                      params: {
                        status: "cancel",
                      },
                    })
                  );
                } else {
                  navigate(-1);
                }
              }}
            >
              확인
            </Button>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  return null;
}
